import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Typography,
  Segmented,
  Empty,
} from "antd";
import { Line, DualAxes } from "@ant-design/charts";
import SectionLoader from "../Loaders/SectionLoader";
import CustomDatePicker from "../Datepicker/CustomDatePicker";
import moment from "moment";
import { get_language, translate } from "../../locale";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import SelfMeasureServices from "../../services/SelfMeasuerServices";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { TableHelper } from "../../helpers/TableHelper";
import RangeForm from "./RangeForm";

const { Title } = Typography;

const BloodGlucoseCharts = () => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState("graph");
  const [dateFrom, setDateFrom] = useState(
    new Date(moment().subtract(7, "days"))
  );
  const [dateTo, setDateTo] = useState(new Date());
  const data = [{ year: "20/10/2022 15:04", value: 0 }];
  const { username } = useParams();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);

  const measureTypes = {
    sistolico: "Presión Sistólica",
    diastolico: "Presión Diastólica",
    fc: "Frecuencia Cardíaca",
    peso: "Peso",
    diabetes: "Glucemia",
    insulina: "Insulina",
    alimentos: "Alimentos",
  };

  const units = {
    // weight
    kg: "kg.",
    lb: "lb.",

    // diabetes
    mgdl: "mg/dl",

    // hypertension
    mmhg: "mg/hg",
    bpm: "bpm",
  };

  const [configGraph, setConfigGraph] = useState({
    data: [{ year: "20/10/2022 15:04", value: 0 }],
    height: 500,
    width: "100%",
    xField: "year",
    yField: "value",

    yAxis: {
      max: 350,
      min: 0,
      tickInterval: 50,
      title: {
        text: "Glucemia (mg/dL)",
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
      {
        geometry: "line",
        color: "#5AD8A6",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 0,
    },
  });

  const [configDaily, setConfigDaily] = useState({
    data: [
      { day: "20/10/2022 15:04", "Glucemia (mg/dL)": 0, HbA1c: 0 },
      { day: "20/10/2022 15:04", "Glucemia (mg/dL)": 0, HbA1c: 0 },
    ],
    height: 500,
    width: "100%",
    xField: "day",
    yField: ["Glucemia (mg/dL)", "HbA1c"],
    yAxis: {
      "Glucemia (mg/dL)": {
        max: 120,
        min: 0,
        tickInterval: 20,
        title: {
          text: "Glucemia (mg/dL)",
        },
      },
      HbA1c: {
        max: 1.2,
        min: 0,
        tickInterval: 20,
        title: {
          text: "HbA1c",
        },
      },
    },
    xAxis: {
      grid: {
        line: {
          style: {
            stroke: "black",
            lineWidth: 1,
            strokeOpacity: 0.2,
            cursor: "pointer",
          },
        },
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
      {
        geometry: "line",
        color: "#5AD8A6",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 10,
    },
  });

  const [configWeekly, setConfigWeekly] = useState({
    data: [data, data],
    height: 500,
    width: "100%",
    xField: "year",
    yField: ["Glucemia (mg/dL)", "HbA1c"],
    yAxis: {
      "Glucemia (mg/dL)": {
        max: 120,
        min: 0,
        tickInterval: 20,
        title: {
          text: "Glucemia (mg/dL)",
        },
      },
      HbA1c: {
        max: 120,
        min: 0,
        tickInterval: 20,
        title: {
          text: "HbA1c",
        },
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
      {
        geometry: "line",
        color: "#5AD8A6",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 10,
    },
  });

  const [dataList, setDataList] = useState([]);

  const columnsList = [
    {
      title: "Fecha",
      dataIndex: "measure_date",
      key: "measure_date",
      render: (_, record) => moment(record.measure_date).format("DD/MM/YYYY"),
      sorter: { multiple: 1 },
    },
    {
      title: "Hora",
      dataIndex: "measure_time",
      key: "measure_time",
      sorter: { multiple: 2 },
    },
    {
      title: "Modo",
      dataIndex: "mode",
      key: "mode",
      render: (_, record) =>
        record.mode === "automatic" ? "Automático" : "Manual",
      sorter: { multiple: 3 },
      filters: [
        {
          text: translate("Automático"),
          value: "automatic",
        },
        {
          text: translate("Manual"),
          value: "manual",
        },
      ],
      onFilter: (value, record) => record.mode.indexOf(value) === 0,
    },
    {
      title: "Valor",
      dataIndex: "value",
      key: "value",
      render: (_, record) => {
        return record.value
          ? `${record.value} ${units[record.unit]}`
          : "No data";
      },
      sorter: { multiple: 4 },
    },
  ];

  useEffect(() => {
    loadGraph();
    loadTotalGraph();
  }, [JSON.stringify(tableParams)]);

  const loadGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getControlHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "diabetes",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const list = res.data.controls.map((i) => {
          const value = i.answers.filter((i) => i.diabetes !== undefined).pop();
          return {
            measure_date: i.measure_date,
            measure_time: i.measure_time,
            mode: i.mode,
            value: value.diabetes ? parseInt(value.diabetes) : "",
            unit: value.unit ?? "",
          };
        });
        setDataList(list);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  // Load ALL data in the graph
  const loadTotalGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: 1000,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getControlHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "diabetes",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const data = res.data.controls.map((i) => ({
          value: parseInt(
            i.answers.filter((i) => i.diabetes !== undefined).pop()?.diabetes
          ),
          year: moment(`${i.measure_date} ${i.measure_time}`)
            .tz("utc")
            .local()
            .format("DD/MM/YYYY HH:mm"),
        }));

        setConfigGraph((v) => ({ ...v, data: data.reverse() }));
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  const loadDailyPattern = async () => {
    setLoading(true);
    try {
      const res = await new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({
            data: [
              // { day: "00", "Glucemia (mg/dL)": 80, HbA1c: 0 },
              // { day: "01", "Glucemia (mg/dL)": 78, HbA1c: 0 },
              // { day: "02", "Glucemia (mg/dL)": 73, HbA1c: 0 },
              // { day: "03", "Glucemia (mg/dL)": 86, HbA1c: 0 },
              // { day: "04", "Glucemia (mg/dL)": 93, HbA1c: 0 },
              // { day: "05", "Glucemia (mg/dL)": 80, HbA1c: 0 },
              // { day: "06", "Glucemia (mg/dL)": 78, HbA1c: 0 },
              // { day: "07", "Glucemia (mg/dL)": 73, HbA1c: 0 },
              // { day: "08", "Glucemia (mg/dL)": 86, HbA1c: 0 },
              // { day: "09", "Glucemia (mg/dL)": 93, HbA1c: 0 },
              // { day: "10", "Glucemia (mg/dL)": 80, HbA1c: 0 },
              // { day: "11", "Glucemia (mg/dL)": 78, HbA1c: 0 },
              // { day: "12", "Glucemia (mg/dL)": 73, HbA1c: 0 },
              // { day: "13", "Glucemia (mg/dL)": 86, HbA1c: 0 },
              // { day: "14", "Glucemia (mg/dL)": 93, HbA1c: 0 },
              // { day: "15", "Glucemia (mg/dL)": 80, HbA1c: 0 },
              // { day: "16", "Glucemia (mg/dL)": 78, HbA1c: 0 },
              // { day: "17", "Glucemia (mg/dL)": 73, HbA1c: 0 },
              // { day: "18", "Glucemia (mg/dL)": 86, HbA1c: 0 },
              // { day: "19", "Glucemia (mg/dL)": 93, HbA1c: 0 },
              // { day: "20", "Glucemia (mg/dL)": 80, HbA1c: 0 },
              // { day: "21", "Glucemia (mg/dL)": 78, HbA1c: 0 },
              // { day: "22", "Glucemia (mg/dL)": 73, HbA1c: 0 },
              // { day: "23", "Glucemia (mg/dL)": 86, HbA1c: 0 },
              // { day: "24", "Glucemia (mg/dL)": 93, HbA1c: 0 },
            ],
          });
        }, 2000);
      });
      setConfigDaily((v) => ({ ...v, data: [res.data, res.data] }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const loadWeeklyPattern = async () => {
    setLoading(true);
    try {
      const res = await new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({
            data: [
              { year: "Domingo", "Glucemia (mg/dL)": 80, HbA1c: 0 },
              { year: "Lunes", "Glucemia (mg/dL)": 78, HbA1c: 0 },
              { year: "Martes", "Glucemia (mg/dL)": 73, HbA1c: 0 },
              { year: "Miércoles", "Glucemia (mg/dL)": 86, HbA1c: 0 },
              { year: "Jueves", "Glucemia (mg/dL)": 93, HbA1c: 0 },
              { year: "Viernes", "Glucemia (mg/dL)": 80, HbA1c: 0 },
              { year: "Sábado", "Glucemia (mg/dL)": 78, HbA1c: 0 },
            ],
          });
        }, 2000);
      });
      setConfigGraph((v) => ({ ...v, data: [res.data, res.data] }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleChangeItem = (value) => {
    setSelectedGraph(value);
    switch (value) {
      case "graph":
        loadTotalGraph();
        loadGraph();
        break;
      case "daily_pattern":
        loadDailyPattern();
        break;
      case "weekly_pattern":
        break;
      case "scattering":
        break;
      case "list":
        loadGraph();
        break;
      case "range":
        break;
    }
  };
  const onChangeDatePicker = (name, value) => {
    if (name === "dateFrom") {
      setDateFrom(value);
    } else if (name === "dateTo") {
      setDateTo(value);
    }
  };
  const handleSearch = (e) => {
    setSearching(true);
    loadTotalGraph();
    loadGraph();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setDataList([]);
      setConfigGraph([]);
    }
  };

  return (
    <>
      <Form onFinish={handleSearch}>
        <Row className="mt-6 mb-14 relative z-10" gutter={[40, 10]}>
          <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
            <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
              <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                {translate("Fecha desde")}
              </div>
              <CustomDatePicker
                locale={get_language}
                selected={dateFrom}
                onChange={(e) => onChangeDatePicker("dateFrom", e)}
              />
            </div>
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
            <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
              <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                {translate("Fecha hasta")}
              </div>
              <CustomDatePicker
                locale={get_language}
                selected={dateTo}
                onChange={(e) => onChangeDatePicker("dateTo", e)}
              />
            </div>
          </Col>
          <Col
            xs={{ span: 24 }}
            md={{ span: 6 }}
            className="text-right md:text-left"
          >
            <Button
              htmlType="submit"
              type="primary"
              className="bg-custom_primary"
              icon={
                searching ? (
                  <LoadingOutlined />
                ) : (
                  <SearchOutlined style={{ position: "relative" }} />
                )
              }
            >
              {translate("Buscar")}
            </Button>
          </Col>
        </Row>
      </Form>
      <Row className="mb-14">
        <Col>
          <Segmented
            options={[
              { value: "graph", label: "Gráfico" },
              // { value: "daily_pattern", label: "Patrón diario" },
              // { value: "weekly_pattern", label: "Patrón semanal" },
              // { value: "scattering", label: "Dispersión" },
              { value: "list", label: "Listado" },
              { value: "range", label: "Rangos" },
            ]}
            onChange={handleChangeItem}
          />
        </Col>
      </Row>
      <Row className="relative mb-14">
        {loading && <SectionLoader />}

        {selectedGraph === "graph" && (
          <Col span={24}>
            <Line {...configGraph} />
          </Col>
        )}
        {selectedGraph === "daily_pattern" && (
          <Col span={24}>
            <DualAxes {...configDaily} />
          </Col>
        )}
        {selectedGraph === "list" && (
          <Col span={24}>
            <Table
              rowKey={(i) => `${i.measure_date} ${i.measure_time}`}
              columns={columnsList}
              dataSource={dataList}
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
              }}
              pagination={{ ...tableParams.pagination, total: total }}
              onChange={handleTableChange}
            />
          </Col>
        )}
        {selectedGraph === "range" && (
          <Col span={24}>
            <Row gutter={[48, 48]}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "diabetes",
                    max_value_alert: 125,
                    max_value: 99,
                    min_value: 60,
                    min_value_alert: 55,
                  }}
                  title={translate("Rango de glucemia")}
                />
              </Col>

              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "register_diabetes",
                    max_value_alert: 30,
                    max_value: 7,
                  }}
                  title={translate("Días sin registros de glucemia")}
                  onlyMax={true}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </>
  );
};

export default BloodGlucoseCharts;
